import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { LoadingButton } from '@mui/lab';

import { CONFIG } from 'src/global-config';

import { Form, Field } from 'src/components/hook-form';
import { useColorScheme, useMediaQuery, useTheme } from '@mui/material';

import { useLocale } from 'src/locales';
import { useBusiness } from 'src/api/business/useBusiness';
import { useEffect, useState } from 'react';
import { notify } from 'src/utils/helper';
import { createSchemas } from 'src/utils/use-validations';
import { QuotationRequestForm } from './quotation-request-form';
// ----------------------------------------------------------------------

const serviceOptions = [
  { value: 'Quotation', label: 'Quotation' },
  { value: 'Transportation Contract', label: 'Transportation_Contract' },
];

export function BusinessServiceForm({ sx, ...other }) {
  const { t, locale } = useLocale();
  const [isQuotationFormVisible, setIsQuotationFormVisible] = useState(false);
  const [formValues, setFormValues] = useState(null);
  const defaultValues = {
    company: '',
    mobile: '',
    service: '',
    email: '',
  };

  const { data: postBusinessData, mutate: postBusiness, isPending } = useBusiness();
  const { serviceFormSchema } = createSchemas();

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const imageSrc = isSmallScreen
    ? `${CONFIG.assetsDir}/assets/images/home/seo/Business-Solutions/shahen-business-solutions-banner-phone.webp`
    : `${CONFIG.assetsDir}/assets/images/home/seo/Business-Solutions/shahen-business-solutions-banner.webp`;

  const methods = useForm({
    resolver: zodResolver(serviceFormSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const getNameForLanguage = (key) => key;

  const onSubmit = handleSubmit(async (data) => {
    try {
      // CALL API TO SUBMIT THE FORM DATA
      setFormValues(data);
      setIsQuotationFormVisible(true);
      postBusiness({ ...data, service: data?.service?.value, language: locale });
    } catch (error) {
      console.error(error);
    }
  });

  useEffect(() => {
    if (postBusinessData) {
      if (postBusinessData?.success) {
        notify(
          'success',
          t('Your_request_has_been_submitted_successfully') ||
            'Your request has been submitted successfully'
        );
        reset();
      } else {
        notify(
          'error',
          t('There_was_an_error_submitting_your_request') ||
            'There was an error submitting your request'
        );
      }
    }
  }, [postBusinessData, reset]);

  const renderForm = () => (
    <Box sx={{ gap: 2.5, display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
      <Field.Text name="company" label={t('Enter_Company_Name')} sx={{ flex: 1 }} />
      <Field.Text name="mobile" label={t('Enter_Mobile_Number')} sx={{ flex: 1 }} />
      <Field.Text name="email" label={t('Enter_Email_Address')} sx={{ flex: 1 }} />

      <LoadingButton
        loading={isSubmitting || isPending}
        size="large"
        type="submit"
        variant="contained"
        color="primary"
        sx={(theme) => ({
          mr: { xs: 0, md: 'auto' },
          ml: { xs: 0, md: 'unset' },
          bgcolor: theme.palette.primary.main,
          flexGrow: { xs: 1, md: 0.5 },
        })}
      >
        {t('next')}
      </LoadingButton>
    </Box>
  );

  const { mode } = useColorScheme();

  return (
    <Box
      component="section"
      sx={[
        {
          pt: { xs: 0, md: 10 },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...other}
    >
      <Container maxWidth={false} disableGutters>
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: { md: 'calc(100vh - 80px)', xs: 'auto' },
          }}
        >
          <Box
            component="img"
            // src={`${CONFIG.assetsDir}/assets/images/home/seo/Business-Solutions/shahen-business-solutions-banner.webp`}
            src={imageSrc}
            alt={t('business-hero-title')}
            title={t('business-hero-meta-desk')}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              position: 'absolute',
              top: 0,
              left: 0,
            }}
          />

          <Container
            sx={{ height: '100%', position: 'relative', bgcolor: 'rgba(0, 0, 0, 0.5)' }}
            maxWidth={false}
          >
            <Box
              sx={{
                position: { md: 'absolute', xs: 'relative' },
                bottom: { xs: '4%', md: '6%' },
                left: 0,
                right: 0,
                zIndex: 1,
                width: '90%',
                mx: 'auto',
                py: { xs: 3, md: 0 },
              }}
            >
              <Container sx={{ p: { xs: 0, md: 2 } }}>
                <Typography
                  variant="h1"
                  sx={{
                    color: mode === 'dark' ? 'text.primary' : 'background.whitePaper',
                    textAlign: 'center',
                    mb: 3,
                    fontSize: { md: '2rem !important', xs: '1.3rem !important' },
                  }}
                >
                  {t('Your_strategic_partner_for_tech_driven_end_to_end_transport_solutions')}
                </Typography>

                <Box
                  sx={[
                    {
                      p: 4,
                      gap: 2.5,
                      borderRadius: 2,
                      bgcolor: 'background.neutral',
                      mx: 'auto',
                    },
                    ...(Array.isArray(sx) ? sx : [sx]),
                  ]}
                  {...other}
                >
                  <Form methods={methods} onSubmit={onSubmit}>
                    {renderForm()}
                  </Form>
                </Box>
              </Container>
            </Box>
          </Container>
        </Box>
      </Container>

      <QuotationRequestForm
        open={isQuotationFormVisible}
        onClose={() => {
          setIsQuotationFormVisible(false);
        }}
        formValues={formValues}
      />
    </Box>
  );
}
